import React, { createContext, useState, useEffect, useCallback } from 'react';
import { loginUser, refreshUserToken } from '../services/UserAuthService';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext();

export default AuthContext;

export const AuthProvider = ({ children }) => {

  const [authTokens, setAuthTokens] = useState(() => {
    const storedTokens = localStorage.getItem('authTokens');
    return storedTokens ? JSON.parse(storedTokens) : null;
  });
  const [userData, setUserData] = useState(() => {
    const storedTokens = localStorage.getItem('authTokens');
    return storedTokens ? jwtDecode(storedTokens) : null;
  });
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const storedTokens = localStorage.getItem('authTokens');
    return !!storedTokens;
  });

  const [loading, setLoading] = useState(true);

  const userLogin = async (data) => {
    const storedTokens = localStorage.getItem('authTokens');
    if (storedTokens) {
      return { success: false, message: 'User is already authenticated' };
    }

    const result = await loginUser(data.email, data.password);
    
    if (result.success) {
      const responseData = result.data;
      setAuthTokens(responseData);
      const decoded_data = jwtDecode(responseData.access);
      setUserData(decoded_data);
      setIsAuthenticated(true);
      localStorage.setItem('authTokens', JSON.stringify(responseData));
      const is_admin = decoded_data.is_admin;
      return { success: true, is_admin: is_admin };
    } else {
      return { success: false, message: result.message };
    }
  };
  
  const userLogout = () => {
    setAuthTokens(null);
    setUserData(null);
    setIsAuthenticated(false);
    localStorage.removeItem('authTokens');
  };

  const updateToken = useCallback(async () => {
    if (!authTokens?.refresh) {
      if (loading) {
        setLoading(false);
      }
      return;
    }

    try {
      const result = await refreshUserToken(authTokens.refresh);

      if (result.success) {
        const data = result.data;
        setAuthTokens(data);
        setUserData(jwtDecode(data.access));
        localStorage.setItem('authTokens', JSON.stringify(data));
      } else {
        console.log('Token refresh failed:', result.message);
        userLogout();
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      userLogout();
    }

    if (loading) {
      setLoading(false);
    }
  }, [authTokens, loading]);

  function getCookie(name) {
    var cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      var cookies = document.cookie.split(';');
      for (var i = 0; i < cookies.length; i++) {
        var cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  }

  const contextData = {
    userData: userData,
    userId: userData?.user_id,
    authTokens: authTokens,
    isAuthenticated: isAuthenticated,
    userLogin: userLogin,
    userLogout: userLogout,
    getCookie: getCookie,
  };

  useEffect(() => {
    if (loading && authTokens) {
      updateToken();
    } else if (loading) {
      setLoading(false);
    }

    // Refresh token every 50 minutes (instead of 4 minutes to avoid conflicts)
    const fiftyMinutes = 1000 * 60 * 50;

    const interval = setInterval(() => {
      if (authTokens?.refresh) {
        updateToken();
      }
    }, fiftyMinutes);

    return () => clearInterval(interval);
  }, [authTokens, loading, updateToken]);

  return <AuthContext.Provider value={contextData}>{children}</AuthContext.Provider>;
};